import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import {
  ChallengeHomeCard,
  ChallengeHomeCardProps,
} from '@fhs/loyalty/src/components/challenge-home-card';
import { ChallengeHomeCardSkeleton } from '@fhs/loyalty/src/components/challenge-home-card-skeleton';
import { createChallengeHomeCardProps } from '@fhs/loyalty/src/utils';
import { OrderDisplaySkeleton, RapidRescueCard } from '@fhs/ui';
import { OrderDisplay } from '@fhs/ui/src/components/order-display';
import { useDebounce } from '@fhs-legacy/frontend/src/hooks/use-debounce';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';

import { ScreenHomeProps } from '../types';

type HomeCardProps = Pick<
  ScreenHomeProps,
  | 'displayedOrder'
  | 'displayedOrderCardLoading'
  | 'redirectPath'
  | 'onReorder'
  | 'reorderLoading'
  | 'logReorderCardPress'
  | 'challengesData'
  | 'isLoadingChallenges'
  | 'loyaltyUserReady'
  | 'isDesktop'
  | 'onPressChallenge'
  | 'showChallengeInfo'
> & {
  style?: StyleProp<ViewStyle>;
};

export function HomeCard({
  style,
  displayedOrderCardLoading,
  displayedOrder,
  challengesData,
  isLoadingChallenges,
  loyaltyUserReady,
  redirectPath,
  reorderLoading,
  onReorder,
  logReorderCardPress,
  isDesktop,
  onPressChallenge,
  showChallengeInfo,
}: HomeCardProps) {
  const containerStyle = [styles.container, style];

  const firstChallenge = challengesData?.[0];
  const challengeHomeCardProps: ChallengeHomeCardProps | undefined = firstChallenge
    ? createChallengeHomeCardProps(firstChallenge, isDesktop, onPressChallenge, showChallengeInfo)
    : undefined;

  const isLoading = isLoadingChallenges || !loyaltyUserReady || displayedOrderCardLoading;
  const debouncedIsLoading = useDebounce(isLoading, 300);
  const { isDelivery } = useServiceModeContext();

  if (isDesktop) {
    if (displayedOrder && displayedOrder.serviceModeTitle !== 'Delivery Order' && !isDelivery) {
      return (
        <View testID="order-display" style={containerStyle}>
          <OrderDisplay
            type={displayedOrder.type}
            href={redirectPath}
            storeName={displayedOrder.storeName}
            items={displayedOrder.items}
            serviceModeTitle={displayedOrder.serviceModeTitle}
            reorderCallback={onReorder}
            reorderLoading={reorderLoading}
            logReorderCardPress={logReorderCardPress}
          />
        </View>
      );
    }
    return null;
  }

  if (isLoadingChallenges || !loyaltyUserReady) {
    return (
      <View testID="challenge-home-skeleton" style={containerStyle}>
        <ChallengeHomeCardSkeleton />
      </View>
    );
  }

  if (debouncedIsLoading) {
    return (
      <View testID="order-display-skeleton" style={containerStyle}>
        <OrderDisplaySkeleton />
      </View>
    );
  }
  if (displayedOrder && displayedOrder.serviceModeTitle !== 'Delivery Order' && !isDelivery) {
    return (
      <View testID="order-display" style={containerStyle}>
        <OrderDisplay
          type={displayedOrder.type}
          href={redirectPath}
          storeName={displayedOrder.storeName}
          items={displayedOrder.items}
          serviceModeTitle={displayedOrder.serviceModeTitle}
          reorderCallback={onReorder}
          reorderLoading={reorderLoading}
          logReorderCardPress={logReorderCardPress}
        />
      </View>
    );
  }

  if (challengeHomeCardProps) {
    return (
      <View testID="challenge-home-card" style={containerStyle}>
        <ChallengeHomeCard {...challengeHomeCardProps} />
      </View>
    );
  }

  return (
    <View testID="rapid-rescue-card" style={[styles.rapidRescueContainer, style]}>
      <RapidRescueCard />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
  },
  rapidRescueContainer: {
    width: '100%',
    paddingHorizontal: 16,
    flex: 1,
    height: 125,
  },
});
